import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Form from 'react-bootstrap/Form';
import FormAlert from 'components/Forms/FormAlert';
import { changePassword, updateEmail } from 'actions/profile';
import DotLoader from 'components/Utlility/DotLoader';
import styles from '../ProfileStyles.module.scss';

const ManageAccount = () => {
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.auth);

    // Email form state
    const [emailForm, setEmailForm] = useState({
        newEmail: '',
        password: ''
    });
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [emailSuccess, setEmailSuccess] = useState('');

    // Password form state
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    // Password requirements
    const [validLength, setValidLength] = useState(false);
    const [hasNumber, setHasNumber] = useState(false);
    const [upperCase, setUpperCase] = useState(false);
    const [lowerCase, setLowerCase] = useState(false);

    // Email handlers
    const handleEmailChange = (e) => {
        const { name, value } = e.target;
        setEmailForm(prev => ({ ...prev, [name]: value }));
        setEmailError('');
        setEmailSuccess('');
    };

    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const validateEmailForm = () => {
        if (!emailForm.newEmail) {
            setEmailError('Please enter a new email address');
            return false;
        }
        if (!validateEmail(emailForm.newEmail)) {
            setEmailError('Please enter a valid email address');
            return false;
        }
        if (emailForm.newEmail.toLowerCase() === user?.email?.toLowerCase()) {
            setEmailError('New email must be different from your current email');
            return false;
        }
        if (!emailForm.password) {
            setEmailError('Please enter your password to confirm');
            return false;
        }
        return true;
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        if (!validateEmailForm()) return;

        setEmailLoading(true);
        setEmailError('');
        setEmailSuccess('');

        try {
            await dispatch(updateEmail(emailForm.newEmail, emailForm.password));
            setEmailSuccess('Email updated successfully!');
            setEmailForm({
                newEmail: '',
                password: ''
            });
        } catch (err) {
            setEmailError(err.Message || 'Failed to update email. Please check your password.');
        } finally {
            setEmailLoading(false);
        }
    };

    // Password handlers
    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordForm(prev => ({ ...prev, [name]: value }));
        setPasswordError('');
        setPasswordSuccess('');

        if (name === 'newPassword') {
            setValidLength(value.length >= 8);
            setHasNumber(/\d/.test(value));
            setUpperCase(/[A-Z]/.test(value));
            setLowerCase(/[a-z]/.test(value));
        }
    };

    const validatePasswordForm = () => {
        if (!passwordForm.currentPassword) {
            setPasswordError('Please enter your current password');
            return false;
        }
        if (!passwordForm.newPassword) {
            setPasswordError('Please enter a new password');
            return false;
        }
        if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
            setPasswordError('New passwords do not match');
            return false;
        }
        if (!validLength || !hasNumber || !upperCase || !lowerCase) {
            setPasswordError('New password does not meet all requirements');
            return false;
        }
        return true;
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (!validatePasswordForm()) return;

        setPasswordLoading(true);
        setPasswordError('');
        setPasswordSuccess('');

        try {
            await dispatch(changePassword(
                passwordForm.currentPassword,
                passwordForm.newPassword,
                passwordForm.confirmNewPassword
            ));
            setPasswordSuccess('Password changed successfully!');
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmNewPassword: ''
            });
            setValidLength(false);
            setHasNumber(false);
            setUpperCase(false);
            setLowerCase(false);
        } catch (err) {
            setPasswordError(err.Message || 'Failed to change password. Please check your current password.');
        } finally {
            setPasswordLoading(false);
        }
    };

    return (
        <div>
            <h2 className={styles.sectionTitle}>Manage Account</h2>
            <p className={styles.sectionDescription}>
                Update your email address or change your password.
            </p>

            {/* Update Email Section */}
            <div className={styles.card}>
                <h3 className={styles.cardTitle}>Update Email</h3>
                <p className={styles.cardSubtitle}>
                    Change the email address associated with your account.
                </p>

                <div className={styles.currentEmailDisplay}>
                    <span className={styles.currentEmailLabel}>Current Email</span>
                    <span className={styles.currentEmailValue}>{user?.email || 'N/A'}</span>
                </div>

                <Form onSubmit={handleEmailSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>New Email Address</Form.Label>
                        <Form.Control
                            type="email"
                            name="newEmail"
                            value={emailForm.newEmail}
                            onChange={handleEmailChange}
                            placeholder="Enter your new email address"
                            autoComplete="email"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Confirm Password</Form.Label>
                        <Form.Control
                            type="password"
                            name="password"
                            value={emailForm.password}
                            onChange={handleEmailChange}
                            placeholder="Enter your password to confirm"
                            autoComplete="current-password"
                        />
                        <Form.Text className="text-muted">
                            Your password is required to verify this change.
                        </Form.Text>
                    </Form.Group>

                    <FormAlert variant="danger">{emailError}</FormAlert>
                    <FormAlert variant="success">{emailSuccess}</FormAlert>

                    <Form.Group>
                        <button type="submit" className={`${styles.button} ${styles.small}`} disabled={emailLoading}>
                            {emailLoading && <DotLoader size="medium" variant="light" />}
                            {emailLoading ? '' : 'Update Email'}
                        </button>
                    </Form.Group>
                </Form>
            </div>

            {/* Change Password Section */}
            <div className={styles.card}>
                <h3 className={styles.cardTitle}>Change Password</h3>
                <p className={styles.cardSubtitle}>
                    Update your account password. You'll need your current password.
                </p>

                <Form onSubmit={handlePasswordSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Current Password</Form.Label>
                        <Form.Control
                            type="password"
                            name="currentPassword"
                            value={passwordForm.currentPassword}
                            onChange={handlePasswordChange}
                            placeholder="Enter your current password"
                            autoComplete="current-password"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>New Password</Form.Label>
                        <Form.Control
                            type="password"
                            name="newPassword"
                            value={passwordForm.newPassword}
                            onChange={handlePasswordChange}
                            placeholder="Enter your new password"
                            autoComplete="new-password"
                            isInvalid={passwordForm.newPassword && (!validLength || !hasNumber || !upperCase || !lowerCase)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <ul className={styles.passwordRequirements}>
                            <li className={validLength ? "text-success" : "text-danger"}>
                                Your password must be at least 8 characters
                            </li>
                            <li className={hasNumber ? "text-success" : "text-danger"}>
                                Your password must include a number
                            </li>
                            <li className={upperCase ? "text-success" : "text-danger"}>
                                Your password must include an upper case letter
                            </li>
                            <li className={lowerCase ? "text-success" : "text-danger"}>
                                Your password must include a lower case letter
                            </li>
                        </ul>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Confirm New Password</Form.Label>
                        <Form.Control
                            type="password"
                            name="confirmNewPassword"
                            value={passwordForm.confirmNewPassword}
                            onChange={handlePasswordChange}
                            placeholder="Confirm your new password"
                            autoComplete="new-password"
                            isInvalid={passwordForm.confirmNewPassword && passwordForm.newPassword !== passwordForm.confirmNewPassword}
                        />
                    </Form.Group>

                    <FormAlert variant="danger">{passwordError}</FormAlert>
                    <FormAlert variant="success">{passwordSuccess}</FormAlert>

                    <Form.Group>
                        <button type="submit" className={`${styles.button} ${styles.small}`} disabled={passwordLoading}>
                            {passwordLoading && <DotLoader size="medium" variant="light" />}
                            {passwordLoading ? '' : 'Update Password'}
                        </button>
                    </Form.Group>
                </Form>
            </div>
        </div>
    );
};

export default ManageAccount;
